// client/src/components/AffiliatesDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import '../CSS/Dashboard.css';
import AffiliatesNavbar from './AffiliatesNavbar';
import { db, storage } from '../../firebase';
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  getDoc,
  where,
  documentId,
  limit,
  // cache-first helpers
  getDocFromCache,
  getDocsFromCache,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaClipboard, FaCheck } from 'react-icons/fa';
import { ref, listAll, getMetadata, getDownloadURL } from 'firebase/storage';

// Graph
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// === Cache session ===
const USERS_CACHE_KEY = 'affdash_users_cache_v1'; // { [uid]: { pseudo, ts } }
const PROMOS_CACHE_PREFIX = 'affdash_promos_by_uid_v3:'; // bump pour inclure __ownerUid
const AFF_UIDS_CACHE_PREFIX = 'affdash_regie_aff_uids_v1:'; // cache des affiliés par régie
const PROMOS_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h
const USERS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const AFF_UIDS_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

// Photo de profil (même logique que la page AffiliatesProfil)
const PROFILE_PHOTO_KEY = (uid) => `profilePhotoURL_${uid}`;
const PROFILE_PHOTO_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

const getSessionJSON = (key, def = null) => {
  try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
};
const setSessionJSON = (key, val) => {
  try { sessionStorage.setItem(key, JSON.stringify(val)); } catch {}
};

const safeLocalStorage = (fn, fallback = null) => {
  try { return fn(); } catch { return fallback; }
};
const fromLocalCache = (key, ttlMs = null) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    if (raw.startsWith('{') || raw.startsWith('[')) {
      const obj = JSON.parse(raw);
      if (!ttlMs) return obj;
      if (!obj || typeof obj !== 'object') return null;
      const ts = typeof obj.ts === 'number' ? obj.ts : 0;
      if (ts && Date.now() - ts > ttlMs) return null;
      return obj;
    }
    return ttlMs ? null : raw;
  } catch {
    return null;
  }
};
const putLocalCache = (key, payload) => {
  try {
    const value = typeof payload === 'string'
      ? payload
      : JSON.stringify({ ...payload, ts: Date.now() });
    localStorage.setItem(key, value);
  } catch {}
};


// =====================
// Parse/temps helpers
// =====================
const safeInt = (val, def = 0) => {
  const n = parseInt(val, 10);
  return Number.isFinite(n) ? n : def;
};
const monthMap = {
  "janvier": 0, "février": 1, "fevrier": 1, "mars": 2, "avril": 3, "mai": 4, "juin": 5,
  "juillet": 6, "août": 7, "aout": 7, "septembre": 8, "octobre": 9, "novembre": 10, "décembre": 11, "decembre": 11,
};

const extractDateTime = (usedTimestamps) => {
  if (!usedTimestamps) return '';
  if (typeof usedTimestamps === "object" && usedTimestamps !== null && usedTimestamps.datetime) {
    return usedTimestamps.datetime;
  }
  const tsString = String(usedTimestamps);
  const dashIndex = tsString.indexOf('-');
  return dashIndex >= 0 ? tsString.substring(dashIndex + 1).trim() : tsString;
};

const parseUsedTimestamp = (timestampStr) => {
  try {
    if (!timestampStr || typeof timestampStr !== 'string') return new Date();
    const raw = timestampStr.trim();

    if (raw.includes('/')) {
      const [datePart, timePartRaw = '00:00:00'] = raw.split(' ');
      const [dayStr, monthStr, yearStr] = (datePart || '').split('/');
      const [hStr = '00', mStr = '00', sStr = '00'] = (timePartRaw || '').split(':');
      const d = new Date(
        safeInt(yearStr, new Date().getFullYear()),
        safeInt(monthStr, 1) - 1,
        safeInt(dayStr, 1),
        safeInt(hStr, 0),
        safeInt(mStr, 0),
        safeInt(sStr, 0)
      );
      return isNaN(d.getTime()) ? new Date() : d;
    }

    if (raw.includes(' à ')) {
      const [datePartRaw, timePartWithZoneRaw] = raw.split(' à ');
      const datePart = (datePartRaw || '').trim();
      const [dayStr, monthName = '', yearStr] = datePart.split(' ');
      const monthKey = monthName.toLowerCase();
      const month = monthMap.hasOwnProperty(monthKey) ? monthMap[monthKey] : 0;
      const timeStr = (timePartWithZoneRaw || '').trim().split(' ')[0] || '';
      const [hStr = '00', mStr = '00', sStr = '00'] = timeStr.split(':');
      const d = new Date(
        safeInt(yearStr, new Date().getFullYear()),
        month,
        safeInt(dayStr, 1),
        safeInt(hStr, 0),
        safeInt(mStr, 0),
        safeInt(sStr, 0)
      );
      return isNaN(d.getTime()) ? new Date() : d;
    }

    const d = new Date(raw);
    return isNaN(d.getTime()) ? new Date() : d;
  } catch {
    return new Date();
  }
};

// =====================
// Firestore helpers (cache-first + in-flight dedupe)
// =====================
const inflight = new Map(); // key -> Promise

const withInflight = async (key, fn) => {
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try { return await fn(); } finally { inflight.delete(key); }
  })();
  inflight.set(key, p);
  return p;
};

const readPromosFromCache = (uid) => {
  const key = PROMOS_CACHE_PREFIX + uid;
  const cached = getSessionJSON(key);
  if (!cached) return null;
  if (Date.now() - (cached.ts || 0) > PROMOS_CACHE_TTL_MS) return null;
  return cached.data || null;
};
const writePromosToCache = (uid, data) => {
  const key = PROMOS_CACHE_PREFIX + uid;
  setSessionJSON(key, { ts: Date.now(), data });
};

const readAffUidsFromCache = (regieUid) => {
  const key = AFF_UIDS_CACHE_PREFIX + regieUid;
  const cached = getSessionJSON(key);
  if (!cached) return null;
  if (Date.now() - (cached.ts || 0) > AFF_UIDS_CACHE_TTL_MS) return null;
  return cached.data || null;
};
const writeAffUidsToCache = (regieUid, data) => {
  const key = AFF_UIDS_CACHE_PREFIX + regieUid;
  setSessionJSON(key, { ts: Date.now(), data });
};

// Cache-first single doc
const getDocSmart = async (ref, { preferCache = true } = {}) => {
  if (preferCache) {
    try {
      const snap = await getDocFromCache(ref);
      return snap;
    } catch {
      // fallthrough to server
    }
  }
  return getDoc(ref);
};


// Retourne { promos, transactions, hasPromo } et **annote** __ownerUid
const getPromoDataForUid = async (uid, { forceFresh = false } = {}) => {
  if (!uid) return { promos: [], transactions: [], hasPromo: false };

  // cache mémoire/session
  if (!forceFresh) {
    const cached = readPromosFromCache(uid);
    if (cached) return cached;
  }

  return withInflight(`promos:${uid}:${forceFresh ? 'fresh' : 'cached'}`, async () => {
    try {
      const userDocRef = doc(db, 'code promo', uid);

      // parent (cache -> serveur si besoin)
      const userDocSnap = await getDocSmart(userDocRef, { preferCache: !forceFresh });

      if (!userDocSnap.exists()) {
        const payload = { promos: [], transactions: [], hasPromo: false };
        writePromosToCache(uid, payload);
        return payload;
      }

      const parentData = userDocSnap.data() || {};
      const parentHas = !!(parentData.promoCode && String(parentData.promoCode).trim() !== '');

      // subcollection codes : cache-first puis serveur
      const codesCollectionRef = collection(userDocRef, 'codes');
      let codesArray = [];
      try {
        // Essaye d’abord le cache; si vide et pas hard reload, évite de lire serveur pour rien
        const qy = query(codesCollectionRef, orderBy("createdAt", "asc"));
        let codesSnapshot = null;

        if (!forceFresh) {
          try { codesSnapshot = await getDocsFromCache(qy); } catch { codesSnapshot = null; }
        }
        if (!codesSnapshot) {
          // si cache vide ou hard reload → serveur
          codesSnapshot = await getDocs(qy);
        }

        for (const docSnap of codesSnapshot.docs) {
          const data = docSnap.data();
          // Charger la sous-collection activations (nouvelle structure)
          const activSnap = await getDocs(collection(docSnap.ref, 'activations'));
          const newActivations = activSnap.docs.map(a => ({ id: a.id, ...a.data() }));
          // Fusionner avec l'ancien tableau usedTimestamps (rétro-compat)
          const oldTs = Array.isArray(data.usedTimestamps) ? data.usedTimestamps
            : (data.usedTimestamps ? [data.usedTimestamps] : []);
          const mergedTs = [...newActivations, ...oldTs];
          codesArray.push({
            id: docSnap.id,
            ...data,
            __ownerUid: uid,
            usedTimestamps: mergedTs.length > 0 ? mergedTs : undefined,
          });
        }
        // garde un tri stable même si createdAt manquant
        codesArray.sort((a,b) => (a?.createdAt?.seconds || 0) - (b?.createdAt?.seconds || 0));
      } catch {
        // fallback minimal si on ne peut pas lister
        try {
          const exQ = query(codesCollectionRef, limit(1));
          let exSnap = null;
          if (!forceFresh) {
            try { exSnap = await getDocsFromCache(exQ); } catch { exSnap = null; }
          }
          if (!exSnap) exSnap = await getDocs(exQ);
          if (!exSnap.empty) codesArray = [{ id: userDocSnap.id, ...parentData, __ownerUid: uid }];
        } catch {
          // ignore
        }
      }

      // 👉 ne compter que les vrais codes avec promoCode non vide
      const codesWithRealPromo = (codesArray || []).filter(
        c => c && c.promoCode && String(c.promoCode).trim() !== ''
      );

      const hasPromo = parentHas || codesWithRealPromo.length > 0;
      const promosOut = codesWithRealPromo.length > 0
        ? codesWithRealPromo
        : (parentHas ? [{ id: userDocSnap.id, ...parentData, __ownerUid: uid }] : []);
      const transactionsOut = promosOut.filter(code => code.usedTimestamps);

      const payload = { promos: promosOut, transactions: transactionsOut, hasPromo };
      writePromosToCache(uid, payload);
      return payload;
    } catch {
      return { promos: [], transactions: [], hasPromo: false };
    }
  });
};

// Récupérer les UIDs affiliés liés à une régie (cache + in-flight)
// ⬇️ Modifié : on inclut aussi les affiliés en attente (affiliatepending) tant qu'ils ne sont pas bannis/suspendus
const getAffiliateUidsForRegie = async (regieUid, { forceFresh = false } = {}) => {
  if (!regieUid) return [];
  if (!forceFresh) {
    const c = readAffUidsFromCache(regieUid);
    if (c && Array.isArray(c)) return c;
  }

  return withInflight(`affuids:${regieUid}:${forceFresh ? 'fresh' : 'cached'}`, async () => {
    const uids = new Set();

    try {
      const colRef = collection(db, 'regies', regieUid, 'affiliates');
      // cache d’abord
      let snap = null;
      if (!forceFresh) {
        try { snap = await getDocsFromCache(colRef); } catch { snap = null; }
      }
      if (!snap) snap = await getDocs(colRef);

      snap.forEach((d) => {
        const id = String(d.id || '').trim();
        const explicit = String((d.data() || {}).affiliateUid || '').trim();
        if (id) uids.add(id);
        if (explicit) uids.add(explicit);
      });
    } catch {}

    if (uids.size === 0) {
      try {
        const q1 = query(collection(db, 'users'), where('regieUid', '==', regieUid));
        let snap = null;
        if (!forceFresh) {
          try { snap = await getDocsFromCache(q1); } catch { snap = null; }
        }
        if (!snap) snap = await getDocs(q1);

        snap.forEach((d) => {
          const role = String((d.data() || {}).role || '').trim();
          if (['affiliates', 'affiliatepending'].includes(role)) {
            uids.add(String(d.id));
          }
        });
      } catch {}
    }

    // 🔎 Filtrer les affiliés éligibles (affiliates + affiliatepending, hors statuts bannis)
    const arr = Array.from(uids);
    const eligible = new Set();
    const allowedRoles = new Set(['affiliates', 'affiliatepending']);
    const blockedStatuses = new Set(['banned', 'suspended', 'disabled', 'archived', 'resiliated']);
    const CHUNK = 10;
    for (let i = 0; i < arr.length; i += CHUNK) {
      const slice = arr.slice(i, i + CHUNK);
      try {
        const qUsers = query(collection(db, 'users'), where(documentId(), 'in', slice));
        let snap = null;
        if (!forceFresh) {
          try { snap = await getDocsFromCache(qUsers); } catch { snap = null; }
        }
        if (!snap) snap = await getDocs(qUsers);

        snap.forEach((d) => {
          const data = d.data() || {};
          const status = String(data.Status || '').trim().toLowerCase();
          const role = String(data.role || '').trim().toLowerCase();
          if (allowedRoles.has(role) && !blockedStatuses.has(status)) {
            eligible.add(d.id);
          }
        });
      } catch {
        // fallback doc par doc
        for (const uid of slice) {
          try {
            const ref = doc(db, 'users', uid);
            let ds = null;
            if (!forceFresh) { try { ds = await getDocFromCache(ref); } catch { ds = null; } }
            if (!ds) ds = await getDoc(ref);
            if (ds.exists()) {
              const data = ds.data() || {};
              const status = String(data.Status || '').trim().toLowerCase();
              const role = String(data.role || '').trim().toLowerCase();
              if (allowedRoles.has(role) && !blockedStatuses.has(status)) {
                eligible.add(uid);
              }
            }
          } catch {}
        }
      }
    }

    const eligibleArr = arr.filter(u => eligible.has(u));
    writeAffUidsToCache(regieUid, eligibleArr);
    return eligibleArr;
  });
};

// === Batch fetch pseudos pour la légende ===
const fetchUserPseudosBatch = async (uids) => {
  if (!Array.isArray(uids) || uids.length === 0) return {};

  // lire cache
  const cache = getSessionJSON(USERS_CACHE_KEY, {}) || {};
  const now = Date.now();
  const miss = uids.filter(u => {
    const rec = cache[u];
    if (!rec) return true;
    if (!rec.ts) return true;
    return now - rec.ts > USERS_CACHE_TTL_MS; // TTL
  });

  const CHUNK = 10; // limite Firestore pour 'in'
  for (let i = 0; i < miss.length; i += CHUNK) {
    const slice = miss.slice(i, i + CHUNK);
    try {
      // cache-first sur la requête "in"
      const qUsers = query(collection(db, 'users'), where(documentId(), 'in', slice));
      let snap = null;
      try { snap = await getDocsFromCache(qUsers); } catch { snap = null; }
      if (!snap) snap = await getDocs(qUsers);

      const seen = new Set();
      snap.forEach(d => {
        const data = d.data() || {};
        cache[d.id] = {
          pseudo: data.pseudo || data.displayName || d.id,
          ts: now,
        };
        seen.add(d.id);
      });
      // manquants → fallback doc par doc (cache-first)
      for (const u of slice) {
        if (seen.has(u)) continue;
        try {
          const ref = doc(db, 'users', u);
          let ds = null;
          try { ds = await getDocFromCache(ref); } catch { ds = null; }
          if (!ds) ds = await getDoc(ref);

          if (ds.exists()) {
            const data = ds.data() || {};
            cache[u] = { pseudo: data.pseudo || data.displayName || u, ts: now };
          } else {
            cache[u] = { pseudo: u, ts: now };
          }
        } catch {
          cache[u] = { pseudo: u, ts: now };
        }
      }
    } catch {
      // si la requête "in" échoue, fallback full doc par doc (cache-first)
      for (const u of slice) {
        try {
          const ref = doc(db, 'users', u);
          let ds = null;
          try { ds = await getDocFromCache(ref); } catch { ds = null; }
          if (!ds) ds = await getDoc(ref);

          if (ds.exists()) {
            const data = ds.data() || {};
            cache[u] = { pseudo: data.pseudo || data.displayName || u, ts: now };
          } else {
            cache[u] = { pseudo: u, ts: now };
          }
        } catch {
          cache[u] = { pseudo: u, ts: now };
        }
      }
    }
  }

  setSessionJSON(USERS_CACHE_KEY, cache);

  const out = {};
  uids.forEach(u => { out[u] = (cache[u] && cache[u].pseudo) || u; });
  return out;
};

// =====================
// Agrégations
// =====================
const aggregateByDay = (txArray) => {
  const dataMap = {};
  (txArray || []).forEach((trans) => {
    let timestamps = [];
    if (typeof trans.usedTimestamps === "string") {
      timestamps = trans.usedTimestamps.split(',').map(s => s.trim()).filter(Boolean);
    } else if (Array.isArray(trans.usedTimestamps)) {
      timestamps = trans.usedTimestamps;
    } else if (trans.usedTimestamps) {
      timestamps = [trans.usedTimestamps];
    }
    timestamps.forEach(ts => {
      const dateString = extractDateTime(ts);
      const transDate = parseUsedTimestamp(dateString);
      const yyyy = transDate.getFullYear();
      const mm = String(transDate.getMonth() + 1).padStart(2, '0');
      const dd = String(transDate.getDate()).padStart(2, '0');
      const key = `${yyyy}-${mm}-${dd}`;
      dataMap[key] = (dataMap[key] || 0) + 1;
    });
  });
  return dataMap;
};

const aggregateByDayPerOwner = (txArray) => {
  // { ownerUid: { 'YYYY-MM-DD': count } }
  const map = {};
  (txArray || []).forEach((trans) => {
    const owner = trans.__ownerUid || '';
    if (!owner) return;
    if (!map[owner]) map[owner] = {};
    let timestamps = [];
    if (typeof trans.usedTimestamps === "string") {
      timestamps = trans.usedTimestamps.split(',').map(s => s.trim()).filter(Boolean);
    } else if (Array.isArray(trans.usedTimestamps)) {
      timestamps = trans.usedTimestamps;
    } else if (trans.usedTimestamps) {
      timestamps = [trans.usedTimestamps];
    }
    timestamps.forEach(ts => {
      const dateString = extractDateTime(ts);
      const transDate = parseUsedTimestamp(dateString);
      const yyyy = transDate.getFullYear();
      const mm = String(transDate.getMonth() + 1).padStart(2, '0');
      const dd = String(transDate.getDate()).padStart(2, '0');
      const key = `${yyyy}-${mm}-${dd}`;
      map[owner][key] = (map[owner][key] || 0) + 1;
    });
  });
  return map;
};

// =====================
// Composant
// =====================
const AffiliatesDashboard = () => {
  const [promos, setPromos] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line
  const [hasPromo, setHasPromo] = useState(false);

  const [copied, setCopied] = useState({});

  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const [isRegie, setIsRegie] = useState(false);
  // eslint-disable-next-line
  const [role, setRole] = useState(null);
  const [userPseudo, setUserPseudo] = useState('');
  const [profilePhotoURL, setProfilePhotoURL] = useState(null);

  // Découpage régie
  const [promosSelf, setPromosSelf] = useState([]);
  const [promosAff, setPromosAff] = useState([]);
  const [transactionsSelf, setTransactionsSelf] = useState([]);
  const [transactionsAff, setTransactionsAff] = useState([]);

  // Pseudos pour la légende des courbes affiliés
  const [userPseudos, setUserPseudos] = useState({});

  // Responsive
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 770);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    let unsub = () => {};
    let mounted = true;

    unsub = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;
      setCurrentUser(user || null);
      setAuthReady(true);
      if (user?.uid) {
        try {
          const uRef = doc(db, 'users', user.uid);
          // cache-first sur le rôle
          let uSnap = null;
          try { uSnap = await getDocFromCache(uRef); } catch { uSnap = null; }
          if (!uSnap) uSnap = await getDoc(uRef);

          const data = uSnap.exists() ? (uSnap.data() || {}) : {};
          const r = uSnap.exists() ? String((data.role || '')).trim() : null;
          setRole(r);
          setIsRegie(r === 'AffiliateRegie');
          setUserPseudo(
            (data.pseudo || data.displayName || user.displayName || user.email || '').toString()
          );
        } catch {
          setRole(null);
          setIsRegie(false);
          setUserPseudo('');
        }
      } else {
        setRole(null);
        setIsRegie(false);
        setUserPseudo('');
        setProfilePhotoURL(null);
      }
    });

    return () => { mounted = false; unsub(); };
  }, []);

  useEffect(() => {
    if (!authReady) return;
    setLoading(!!currentUser?.uid);
  }, [authReady, currentUser?.uid]);

  const uid = currentUser ? currentUser.uid : null;

  // Photo de profil (logo) — cache puis rafraîchissement silencieux
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;

    const loadPhoto = async () => {
      const cached = fromLocalCache(PROFILE_PHOTO_KEY(uid), PROFILE_PHOTO_TTL_MS);
      if (cached && !cancelled) {
        const url = typeof cached === 'string' ? cached : cached.url;
        if (url) setProfilePhotoURL((prev) => prev || url);
      } else {
        const fallback = safeLocalStorage(() => localStorage.getItem('profilePhotoURL'), null);
        if (fallback && !cancelled) setProfilePhotoURL((prev) => prev || fallback);
      }

      if (cached) return; // cache valide → pas de lecture Storage
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;

      try {
        const folderRef = ref(storage, `${uid}/photo-profil/`);
        const result = await listAll(folderRef);
        if (!result.items.length) {
          if (!cancelled) setProfilePhotoURL(null);
          putLocalCache(PROFILE_PHOTO_KEY(uid), { url: null });
          return;
        }

        const itemsWithMetadata = await Promise.all(
          result.items.map(async (item) => {
            try {
              const metadata = await getMetadata(item);
              const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : 0;
              return { item, ts: timeCreated };
            } catch {
              return { item, ts: 0 };
            }
          })
        );

        itemsWithMetadata.sort((a, b) => b.ts - a.ts);
        const latestItem = itemsWithMetadata[0]?.item;
        if (!latestItem) return;

        const url = await getDownloadURL(latestItem);
        if (!cancelled) {
          setProfilePhotoURL(url);
          putLocalCache(PROFILE_PHOTO_KEY(uid), { url });
          safeLocalStorage(() => localStorage.setItem('profilePhotoURL', url));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('AffiliatesDashboard: erreur lors du chargement de la photo de profil', error);
        }
      }
    };

    loadPhoto();
    return () => { cancelled = true; };
  }, [uid]);

  // Chargement principal
  useEffect(() => {
    const run = async () => {
      if (!uid) {
        setLoading(false);
        return;
      }
      const forceFresh = true; // 🔧 forcer la relecture fraîche pour refléter immédiatement les bascules de rôle

      if (!isRegie) {
        const mine = await getPromoDataForUid(uid, { forceFresh });
        setPromos(mine.promos);
        setTransactions(mine.transactions);
        setHasPromo(mine.hasPromo);

        setPromosSelf(mine.promos);
        setTransactionsSelf(mine.transactions);
        setPromosAff([]);
        setTransactionsAff([]);

        setLoading(false);
        return;
      }

      const mine = await getPromoDataForUid(uid, { forceFresh });
      const linkedUids = await getAffiliateUidsForRegie(uid, { forceFresh });

      let allAffPromos = [];
      let allAffTx = [];
      let affHas = false;

      if (linkedUids.length > 0) {
        const settled = await Promise.allSettled(
          linkedUids.map(aUid => getPromoDataForUid(aUid, { forceFresh }))
        );
        settled.forEach((r) => {
          if (r.status === 'fulfilled') {
            const d = r.value || { promos: [], transactions: [], hasPromo: false };
            if (d.promos?.length) allAffPromos.push(...d.promos);
            if (d.transactions?.length) allAffTx.push(...d.transactions);
            if (d.hasPromo) affHas = true;
          }
        });
      }

      const open = !!(mine.hasPromo || affHas);
      setHasPromo(open);

      setPromosSelf(mine.promos);
      setTransactionsSelf(mine.transactions);
      setPromosAff(allAffPromos);
      setTransactionsAff(allAffTx);

      setPromos([...(mine.promos || []), ...allAffPromos]);
      setTransactions([...(mine.transactions || []), ...allAffTx]);

      setLoading(false);
    };
    run();
  }, [uid, isRegie]);

  // ====== Agrégations pour l’affichage ======
  // Jour par jour
  const selfMap = useMemo(() => aggregateByDay(transactionsSelf), [transactionsSelf]);
  const affPerOwnerMap = useMemo(() => aggregateByDayPerOwner(transactionsAff), [transactionsAff]);

  // Labels unifiés
  const unifiedLabels = useMemo(() => {
    const keys = new Set([
      ...Object.keys(selfMap || {}),
      ...Object.values(affPerOwnerMap).flatMap((o) => Object.keys(o || {}))
    ]);
    const arr = Array.from(keys).sort();
    if (arr.length === 0) return [new Date().toISOString().split('T')[0]];
    return arr;
  }, [selfMap, affPerOwnerMap]);

  // Comptes journaliers
  const dailySelf = useMemo(() => unifiedLabels.map(k => selfMap[k] || 0), [unifiedLabels, selfMap]);

  // Une ligne par affilié (journalier)
  const perAffiliateDailySeries = useMemo(() => {
    const obj = {};
    Object.entries(affPerOwnerMap).forEach(([ownerUid, dayMap]) => {
      obj[ownerUid] = unifiedLabels.map(day => dayMap[day] || 0);
    });
    return obj;
  }, [affPerOwnerMap, unifiedLabels]);

  // === Récupérer les pseudos pour les ownerUid des séries affiliés ===
  useEffect(() => {
    const ownerUids = Object.keys(perAffiliateDailySeries || {});
    if (ownerUids.length === 0) return;

    let cancelled = false;
    (async () => {
      const pseudos = await fetchUserPseudosBatch(ownerUids);
      if (!cancelled) {
        setUserPseudos(prev => ({ ...prev, ...pseudos }));
      }
    })();

    return () => { cancelled = true; };
  }, [perAffiliateDailySeries]);

  // ====== Compteurs & métriques ======
  const aggregateTotalUsageNonRegie = useMemo(() => {
    const dataMap = aggregateByDay(transactions);
    const labels = Array.from(new Set(Object.keys(dataMap))).sort();
    const counts = labels.map(k => dataMap[k] || 0);
    return { labels, counts };
  }, [transactions]);

  const now = new Date();
  const countMonth = (arr) => {
    let count = 0;
    (arr || []).forEach((trans) => {
      let timestamps = [];
      if (typeof trans.usedTimestamps === "string") {
        timestamps = trans.usedTimestamps.split(',').map(s => s.trim()).filter(Boolean);
      } else if (Array.isArray(trans.usedTimestamps)) {
        timestamps = trans.usedTimestamps;
      } else if (trans.usedTimestamps) {
        timestamps = [trans.usedTimestamps];
      }
      timestamps.forEach(ts => {
        const dateString = extractDateTime(ts);
        const d = parseUsedTimestamp(dateString);
        if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) count++;
      });
    });
    return count;
  };
  // eslint-disable-next-line
  const monthTxSelf = useMemo(() => countMonth(transactionsSelf), [transactionsSelf]);
  // eslint-disable-next-line
  const monthTxAff  = useMemo(() => countMonth(transactionsAff),  [transactionsAff]);

  const activeCodesSelf = (promosSelf || []).filter(p => p.promoCode && String(p.promoCode).trim() !== '').length;
  const activeCodesAff  = (promosAff  || []).filter(p => p.promoCode && String(p.promoCode).trim() !== '').length;

  const totalUtilSelf = useMemo(() => dailySelf.reduce((a,b)=>a+b,0), [dailySelf]);
  const totalUtilAff  = useMemo(() => {
    const perAff = Object.values(perAffiliateDailySeries);
    return perAff.reduce((sum, arr) => sum + arr.reduce((a,b)=>a+b,0), 0);
  }, [perAffiliateDailySeries]);

  // ====== Graph datasets ======
  const affiliatePalette = (index) => {
    const hue = (index * 47) % 360;
    return `hsl(${hue}, 70%, 45%)`;
  };

  const isEmptyGraph =
    (!isRegie && (aggregateTotalUsageNonRegie.labels.length === 0)) ||
    (isRegie && unifiedLabels.length === 1 && dailySelf[0] === 0 && Object.values(perAffiliateDailySeries).every(a => (a[0] || 0) === 0));

  const chartData = !isRegie
    ? {
        labels: (aggregateTotalUsageNonRegie.labels.length === 0)
          ? [new Date().toISOString().split('T')[0]]
          : aggregateTotalUsageNonRegie.labels,
        datasets: [
          {
            label: "Utilisations",
            data: (aggregateTotalUsageNonRegie.labels.length === 0) ? [0] : aggregateTotalUsageNonRegie.counts,
            fill: false,
            borderColor: "rgba(39,76,116,1)",
            backgroundColor: "rgba(39,76,116,0.18)",
            tension: 0.12,
            pointRadius: (ctx) => (ctx.raw && ctx.raw > 0 ? (isMobile ? 2 : 3) : 0),
          },
        ],
      }
    : (() => {
        const datasets = [];

        // 1) Moi (régie) — daily
        datasets.push({
          label: "Vous",
          data: dailySelf,
          fill: false,
          borderColor: "rgba(39,76,116,1)",
          backgroundColor: "rgba(39,76,116,0.18)",
          borderWidth: 2,
          tension: 0.18,
          pointRadius: (ctx) => (ctx.raw && ctx.raw > 0 ? (isMobile ? 2 : 3) : 0),
        });

        // 2) Une courbe par affilié — daily, **label = pseudo**
        const ownerUids = Object.keys(perAffiliateDailySeries);
        ownerUids.forEach((ownerUid, idx) => {
          const pseudo = userPseudos[ownerUid];
          const label = pseudo
            ? `${pseudo}`
            : `Affilié ${ownerUid.substring(0,6)}…`;
          datasets.push({
            label,
            data: perAffiliateDailySeries[ownerUid],
            fill: false,
            borderColor: affiliatePalette(idx),
            backgroundColor: 'transparent',
            borderWidth: 1.5,
            tension: 0.18,
            pointRadius: (ctx) => (ctx.raw && ctx.raw > 0 ? 2 : 0),
          });
        });

        return {
          labels: unifiedLabels,
          datasets
        };
      })();

  const displayOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        bodyFont: { size: isMobile ? 10 : 12 },
        titleFont: { size: isMobile ? 11 : 13 },
      },
      title: { display: false },
    },
    maintainAspectRatio: false,
    layout: {
      padding: isMobile ? { left: 4, right: 4, top: 4, bottom: 4 } : { left: 10, right: 10, top: 8, bottom: 8 },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: { size: isMobile ? 10 : 12 },
          maxTicksLimit: isMobile ? 5 : 8,
        },
        title: { display: true, text: 'Utilisations', font: { size: isMobile ? 11 : 13 } },
        grid: { drawBorder: false },
      },
      x: {
        ticks: {
          callback: function (value, index) {
            return this.chart.data.labels[index];
          },
          font: { size: isMobile ? 9 : 12 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: isMobile ? 6 : 14,
        },
        grid: { display: false, drawBorder: false },
      },
    },
  };

  // Nom / initiales pour l’avatar héro
  const heroName = useMemo(() => {
    if (userPseudo && userPseudo.trim()) return userPseudo.trim();
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.email) return currentUser.email.split('@')[0];
    return isRegie ? 'Espace régie' : 'Espace affilié';
  }, [userPseudo, currentUser, isRegie]);

  const heroInitials = useMemo(() => {
    const letters = (heroName || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
    return letters || (isRegie ? 'RG' : 'AF');
  }, [heroName, isRegie]);

  // ===== UI Styles =====
  const AD_CSS = `
    .ad-page { width: 100%; display: flex; flex-direction: column; gap: 24px; word-break: break-word; }
    .ad-page .eb-page-header { margin-bottom: 0; align-items: center; justify-content: flex-start; flex-wrap: nowrap; gap: 16px; }
    .ad-page .eb-page-title { margin: 0; text-align: left; }
    .ad-avatar { width: 56px; height: 56px; border-radius: var(--eb-radius); background: var(--eb-brand-50); border: 1px solid var(--eb-brand-100); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; color: var(--eb-brand); font-weight: 700; font-size: 20px; }
    .ad-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .ad-section-label { margin: 0 0 12px; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--eb-muted); text-align: left; }
    .ad-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 16px; }
    .ad-kpi { padding: 20px; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .ad-kpi__label { font-size: 13.5px; font-weight: 500; color: var(--eb-muted); }
    .ad-kpi__value { font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); line-height: 1.2; }
    .ad-kpi__hint { font-size: 13px; color: var(--eb-muted); }
    .ad-kpi--status .ad-kpi__value { font-size: 20px; }
    .ad-main { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 20px; align-items: start; }
    .ad-panel { padding: 24px; display: flex; flex-direction: column; gap: 12px; min-width: 0; }
    .ad-panel__head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: baseline; gap: 4px 12px; margin-bottom: 4px; }
    .ad-panel__title { margin: 0; font-size: 17px; font-weight: 650; color: var(--eb-text); text-align: left; }
    .ad-panel__sub { font-size: 13px; color: var(--eb-muted); }
    .ad-chart { position: relative; height: 300px; }
    .ad-empty { text-align: center; color: var(--eb-muted); font-size: 14px; padding: 12px; }
    .ad-promo { border: 1px solid var(--eb-border); background: var(--eb-surface-2); border-radius: var(--eb-radius); padding: 12px 14px; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
    .ad-promo--self { border-left: 3px solid var(--eb-brand); }
    .ad-promo--aff { border-left: 3px solid var(--eb-success); }
    .ad-promo__title { font-weight: 650; font-size: 14px; color: var(--eb-text); }
    .ad-promo__link { font-size: 13px; color: var(--eb-muted); margin-top: 2px; word-break: break-all; }
    .ad-promo__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .ad-copy { width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center; border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border-strong); background: var(--eb-surface); color: var(--eb-text-2); cursor: pointer; transition: background-color .15s ease, color .15s ease; }
    .ad-copy:hover { background: var(--eb-brand-50); color: var(--eb-brand); }
    .ad-copy--done { color: var(--eb-success); border-color: #c6f0da; background: var(--eb-success-bg); }
    .dashboard-container .ad-subhead { margin: 12px 0 0; font-size: 14px; font-weight: 650; color: var(--eb-text-2); text-align: left; }
    .promo-code-wrapper { word-break: break-word; }
    @media (max-width: 1100px) {
      .ad-main { grid-template-columns: minmax(0, 1fr); }
    }
    @media (max-width: 768px) {
      .ad-page { gap: 20px; }
      .ad-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
      .ad-kpi:last-child:nth-child(odd) { grid-column: 1 / -1; }
      .ad-kpi { padding: 14px; }
      .ad-kpi__value { font-size: 22px; }
      .ad-kpi--status .ad-kpi__value { font-size: 18px; }
      .ad-panel { padding: 16px; }
      .ad-chart { height: 240px; }
      .ad-main-chart { order: 2; }
      .ad-main-promos { order: 1; }
      .ad-promo__actions { width: 100%; justify-content: space-between; }
    }
  `;

  // ===== Liens d’inscription =====
  const isBrowser = typeof window !== 'undefined';
  const signupBase =
    (isBrowser && window.location.hostname === 'localhost')
      ? 'http://localhost:3100/redirectionabonnement'
      : 'https://ecobill.app/redirectionabonnement';

  const buildSignupUrl = (code) => `${signupBase}/${encodeURIComponent(code || '')}`;

  const handleCopy = async (code) => {
    const url = buildSignupUrl(code);
    try {
      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(prev => ({ ...prev, [code]: true }));
        setTimeout(() => setCopied(prev => ({ ...prev, [code]: false })), 2000);
      } else {
        throw new Error('Clipboard API indisponible');
      }
    } catch {
      alert(`Impossible de copier automatiquement. Copiez l’URL manuellement : ${url}`);
    }
  };

  // ===== Rendu =====
  const totalCodesActifsSelf = activeCodesSelf;
  const totalCodesActifsAff  = activeCodesAff;

  const monthTxCountNonRegie = useMemo(() => {
    if (isRegie) return 0;
    const now = new Date();
    let count = 0;
    (transactions || []).forEach((trans) => {
      let timestamps = [];
      if (typeof trans.usedTimestamps === "string") {
        timestamps = trans.usedTimestamps.split(',').map(s => s.trim()).filter(Boolean);
      } else if (Array.isArray(trans.usedTimestamps)) {
        timestamps = trans.usedTimestamps;
      } else if (trans.usedTimestamps) {
        timestamps = [trans.usedTimestamps];
      }
      timestamps.forEach(ts => {
        const dateString = extractDateTime(ts);
        const d = parseUsedTimestamp(dateString);
        if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) {
          count++;
        }
      });
    });
    return count;
  }, [transactions, isRegie]);

  const monthTxCount = !isRegie ? monthTxCountNonRegie : (monthTxSelf + monthTxAff);

  // ✅ nouvel indicateur fiable basé sur de vrais codes actifs
  const computedHasPromo = useMemo(() => {
    if (!isRegie) return activeCodesSelf > 0;
    return (activeCodesSelf + activeCodesAff) > 0;
  }, [isRegie, activeCodesSelf, activeCodesAff]);

  const renderPromoItem = (p, variant, baseHint) => {
    const isBase = p.percentage === 0 && p.MonthNumber === 0;
    const titre = `${isBase ? 'Code de base' : 'Code remisé'} - ${p.promoCode}`;
    const hint = isBase ? baseHint : `- ${p.percentage}% pendant ${p.MonthNumber} mois`;
    return (
      <div key={p.id} className={`ad-promo${variant ? ` ad-promo--${variant}` : ''}`}>
        <div className="promo-code-wrapper" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 200px' }}>
          <div className="ad-promo__title">{titre}</div>
          <div className="ad-promo__link">Lien redirection : {buildSignupUrl(p.promoCode)}</div>
        </div>
        <div className="ad-promo__actions">
          <span className={`eb-badge${variant === 'aff' || !variant ? ' eb-badge--success' : ''}`}>{hint}</span>
          <button
            className={`copy-button ad-copy${copied[p.promoCode] ? ' ad-copy--done' : ''}`}
            onClick={() => handleCopy(p.promoCode)}
            title="Copier le lien d’inscription"
            aria-label="Copier le lien d’inscription"
          >
            {copied[p.promoCode] ? <FaCheck /> : <FaClipboard />}
          </button>
        </div>
      </div>
    );
  };

  const kpi = (label, value, hint, extraClass = '') => (
    <div className={`eb-card ad-kpi ${extraClass}`}>
      <div className="ad-kpi__label">{label}</div>
      <div className="ad-kpi__value">{loading ? '—' : value}</div>
      {hint ? <div className="ad-kpi__hint">{hint}</div> : null}
    </div>
  );

  const loadingBlock = (
    <div className="ad-empty" style={{ display: 'flex', justifyContent: 'center' }}>
      <span className="eb-spinner eb-spinner--sm" aria-label="Chargement…" />
    </div>
  );

  return (
    <div>
      <AffiliatesNavbar />
      <div className="dashboard-container">
        <style>{AD_CSS}</style>

        <div className="ad-page">
          {/* En-tête */}
          <header className="eb-page-header">
            <div className="ad-avatar" aria-hidden>
              {profilePhotoURL ? (
                <img src={profilePhotoURL} alt="Logo de profil" />
              ) : (
                <span>{heroInitials}</span>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="eb-page-title">{isRegie ? 'Espace Régie' : 'Espace Affilié'}</h1>
              <p className="eb-page-subtitle">
                {isRegie ? 'Vos chiffres & courbes (Régie + Affiliates)' : 'Synthèse de vos performances'}
              </p>
            </div>
          </header>

          {/* Rangée 1 — TES métriques */}
          <section>
            {isRegie && <h2 className="ad-section-label">Vos chiffres</h2>}
            <div className="ad-kpis">
              {kpi('Vos Codes actifs', totalCodesActifsSelf)}
              {kpi('Vos Utilisations', totalUtilSelf)}
              {kpi('Vos Transactions (mois)', monthTxSelf)}
            </div>
          </section>

          {/* Rangée 2 — Cumuls affiliés (visible en régie) */}
          {isRegie && (
            <section>
              <h2 className="ad-section-label">Vos affiliés</h2>
              <div className="ad-kpis">
                {kpi('Codes actifs — Affiliates', totalCodesActifsAff)}
                {kpi('Utilisations — Affiliates', totalUtilAff)}
                {kpi('Transactions (mois) — Affiliates', monthTxAff)}
              </div>
            </section>
          )}

          {/* METRICS simples */}
          <section>
            {isRegie && <h2 className="ad-section-label">Synthèse</h2>}
            <div className="ad-kpis">
              <div className="eb-card ad-kpi ad-kpi--status">
                <div className="ad-kpi__label">État du compte</div>
                <div className="ad-kpi__value">
                  {loading ? '—' : (
                    <span className={`eb-badge ${computedHasPromo ? 'eb-badge--success' : 'eb-badge--warning'}`} style={{ fontSize: 14, padding: '4px 12px' }}>
                      {computedHasPromo ? 'Activé' : 'En attente'}
                    </span>
                  )}
                </div>
                <div className="ad-kpi__hint">
                  {computedHasPromo
                    ? (isRegie ? 'Codes présents chez vous ou vos affiliés' : 'Vos codes sont prêts à l’usage')
                    : 'Activez votre compte en complétant votre profil'}
                </div>
              </div>

              {!isRegie ? (
                <>
                  {kpi('Codes promo générés', (promos || []).filter(p => p.promoCode && String(p.promoCode).trim() !== '').length, 'Codes avec promoCode non vide')}
                  {kpi('Transactions ce mois-ci', monthTxCount, 'Comptées via vos codes')}
                </>
              ) : (
                <>
                  {kpi('Codes actifs — Total', activeCodesSelf + activeCodesAff, 'Régie + Affiliés')}
                  {kpi('Utilisations — Total', totalUtilSelf + totalUtilAff, 'Somme de toutes les utilisations')}
                  {kpi('Transactions ce mois — Total', monthTxSelf + monthTxAff, 'Régie + Affiliés')}
                </>
              )}
            </div>
          </section>

          {/* GRAPH + PROMO LIST */}
          <div className="ad-main">
            {/* Graphe */}
            <div className="eb-card ad-panel ad-main-chart">
              <div className="ad-panel__head">
                <h2 className="ad-panel__title">Évolution des utilisations</h2>
                <span className="ad-panel__sub">
                  {!isRegie
                    ? 'Derniers enregistrements'
                    : 'Votre courbe + une courbe par affilié (libellés = pseudo)'}
                </span>
              </div>
              <div className="ad-chart">
                <Line data={chartData} options={displayOptions} />
              </div>
              {isEmptyGraph && <div className="ad-empty">Pas encore d’utilisations enregistrées</div>}
            </div>

            {/* Codes promo */}
            <div className="eb-card ad-panel ad-main-promos">
              {!isRegie ? (
                <>
                  <h2 className="ad-panel__title">Vos codes promo</h2>
                  {loading ? loadingBlock : computedHasPromo ? (
                    promos
                      .filter(p => p?.promoCode && String(p.promoCode).trim() !== '')
                      .map(p => renderPromoItem(p, null, 'Liaison client → affilié'))
                  ) : (
                    <div className="eb-alert eb-alert--info" style={{ justifyContent: 'center', fontWeight: 600 }}>
                      En attente de validation du compte
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 className="ad-panel__title">Mes codes (régie)</h2>
                  {loading ? loadingBlock : (promosSelf || []).filter(p => p?.promoCode && String(p.promoCode).trim() !== '').length > 0 ? (
                    (promosSelf || [])
                      .filter(p => p?.promoCode && String(p.promoCode).trim() !== '')
                      .map(p => renderPromoItem(p, 'self', 'Liaison client → régie'))
                  ) : (
                    <div className="eb-muted" style={{ fontSize: 14 }}>Aucun code régie.</div>
                  )}

                  <h3 className="ad-subhead">Codes de mes affiliates</h3>
                  {(promosAff || []).filter(p => p?.promoCode && String(p.promoCode).trim() !== '').length > 0 ? (
                    (promosAff || [])
                      .filter(p => p?.promoCode && String(p.promoCode).trim() !== '')
                      .map(p => renderPromoItem(p, 'aff', 'Liaison client → affilié'))
                  ) : (
                    <div className="eb-muted" style={{ fontSize: 14 }}>Aucun code affilié.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliatesDashboard;
